import React from "react";
import { Link } from "react-router-dom";

const Terms = () => {
  return (
    <div>
      <nav className="navbar navbar-default navbar-fixed-top">
        <div className="container">
          <div className="navbar-header">
            <Link className="navbar-brand" to="/">
              By The App
            </Link>
          </div>
        </div>
      </nav>

      <main className="privacy-page">
        <div className="container">
          <div className="privacy-page-inner">
            <h1>Terms and Conditions for SMS &amp; Voice Services</h1>
            <p className="privacy-effective">Effective Date: January 01, 2026</p>

            <p>
              By providing your phone number and opting in to receive communications from
              BY THE APP, you agree to the following Terms and Conditions.
            </p>

            <h2>1. Program Description</h2>
            <p>
              BY THE APP provides AI voice agents and business automation for vertical
              industries (for example, restaurants, clinics, and service businesses).
              Depending on the business you contact, our platform may handle phone orders,
              appointments, inquiries, payments, and operational notifications on behalf
              of that business.
            </p>
            <p>
              Customers may receive SMS/MMS messages or voice calls regarding their
              account, inquiries, or transactions. Examples include:
            </p>
            <ul>
              <li>
                <strong>Restaurant ordering:</strong> A customer places an order by phone;
                the business may send a one-time SMS with a secure payment link to complete
                the order, plus order confirmation or status updates.
              </li>
              <li>
                <strong>Business operations:</strong> A business may receive SMS
                notifications to staff or owners (for example, alerts for certain order
                types or high-priority requests). Business users opt in when their account
                is configured with BY THE APP.
              </li>
            </ul>
            <p>
              BY THE APP acts as the technology provider powering these communications for
              the business you interact with. Message content and frequency depend on your
              request and the services of that business.
            </p>

            <h2>2. User Opt-In</h2>
            <p>Users can opt-in to our services by:</p>
            <ul>
              <li>
                Entering a phone number on our website{" "}
                <a href="https://www.bytheapp.com">https://www.bytheapp.com</a> and checking
                the consent box.
              </li>
              <li>Sending a keyword (e.g., &quot;START&quot; or &quot;JOIN&quot;) to our phone number.</li>
              <li>Verbally requesting assistance during a phone call.</li>
            </ul>
            <p>
              By opting in, you represent that you are the owner or authorized user of the
              mobile device and phone number provided.
            </p>

            <h2>3. Sample Phone Script — Voice Opt-In for SMS (Twilio Compliance)</h2>
            <p>
              The following is the exact conversation flow used by ByTheApp AI Voice when
              a customer requests a service that requires SMS (for example, sending a payment
              link after a restaurant phone order). The{" "}
              <strong>SMS consent disclosure</strong> must be read before any text message
              is sent.
            </p>

            <h3>Example: Restaurant phone order with SMS payment link</h3>
            <div className="terms-script" role="region" aria-label="Sample phone script">
              <p>
                <strong>AI:</strong> Thank you for calling Sunrise Bistro. I&apos;m the
                ByTheApp AI assistant. Are you placing an order for pickup or delivery
                today?
              </p>
              <p>
                <strong>Customer:</strong> Pickup, please.
              </p>
              <p>
                <strong>AI:</strong> Great. What would you like to order?
              </p>
              <p>
                <strong>Customer:</strong> One chicken sandwich and a side salad.
              </p>
              <p>
                <strong>AI:</strong> Got it — one chicken sandwich and one side salad for
                pickup. Your total is twenty-four dollars and fifty cents. To complete your
                order, we can text a secure payment link to your mobile phone. What is the
                best mobile number to send that text to?
              </p>
              <p>
                <strong>Customer:</strong> 555-123-4567.
              </p>
              <p className="terms-script-consent">
                <strong>AI (SMS consent — required before sending):</strong> By providing
                this number, you agree to receive SMS messages from Sunrise Bistro powered
                by ByTheApp, including a one-time payment link to complete your order and
                order-related updates for this request. Message frequency varies. Message
                and data rates may apply. Reply STOP to opt out at any time. Reply HELP for
                help. Privacy policy is available at bytheapp.com/privacy. Do you agree to
                receive these text messages? Please say &quot;yes&quot; to confirm.
              </p>
              <p>
                <strong>Customer:</strong> Yes.
              </p>
              <p>
                <strong>AI:</strong> Thank you. You&apos;ll receive a text shortly with
                your secure payment link. Once payment is received, we&apos;ll confirm
                your pickup time. Is there anything else I can help you with today?
              </p>
              <p>
                <strong>Customer:</strong> No, that&apos;s all.
              </p>
              <p>
                <strong>AI:</strong> Thank you for calling Sunrise Bistro. Have a great
                day.
              </p>
            </div>

            <h3>Example: Business owner / staff notification (operational SMS)</h3>
            <p>
              When a business enables ByTheApp AI Voice, authorized staff or owners may
              opt in during setup to receive operational SMS alerts (for example,
              notification when a catering order or large order is placed). Opt-in is
              collected in writing or verbally during onboarding, with the same STOP and
              HELP instructions.
            </p>
            <div className="terms-script" role="region" aria-label="Business staff opt-in script">
              <p className="terms-script-consent">
                <strong>Onboarding disclosure (staff/owner):</strong> By providing your
                mobile number, you agree to receive operational SMS alerts from [Business
                Name] powered by ByTheApp, including order notifications and business
                updates related to your account. Message frequency varies. Message and
                data rates may apply. Reply STOP to opt out. Reply HELP for help. Privacy
                policy: bytheapp.com/privacy. Do you agree? Please confirm yes or no.
              </p>
            </div>

            <h2>4. Message Frequency</h2>
            <p>
              Message frequency varies based on user interaction and the nature of the
              support request. You will receive messages only as needed to fulfill your
              requests or provide service updates.
            </p>

            <h2>5. Cost</h2>
            <p>
              BY THE APP does not charge for our SMS service. However, message and data
              rates may apply from your wireless carrier. Check with your mobile operator
              for details on your texting plan.
            </p>

            <h2>6. How to Opt-Out (Stop)</h2>
            <p>You can cancel the SMS service at any time. To stop receiving messages:</p>
            <ul>
              <li>Text STOP to 18772417545.</li>
            </ul>
            <p>
              After you send the SMS message STOP to us, we will send you a final SMS
              message to confirm that you have been unsubscribed. After this, you will no
              longer receive SMS messages from us unless you re-opt in.
            </p>

            <h2>7. How to Get Help</h2>
            <p>If you are experiencing issues with the messaging program or need assistance, you can:</p>
            <ul>
              <li>Text HELP to 18772417545.</li>
              <li>
                Email us at:{" "}
                <a href="mailto:contact@bytheapp.com">contact@bytheapp.com</a>.
              </li>
              <li>Call us at: (650) 691-8695.</li>
            </ul>

            <h2>8. Carrier Liability</h2>
            <p>
              Carriers (such as AT&amp;T, T-Mobile, Verizon, etc.) are not liable for delayed
              or undelivered messages.
            </p>

            <h2>9. Privacy</h2>
            <p>
              Your use of this service is also governed by our Privacy Policy, which can be
              found at:{" "}
              <Link to="/privacy">https://www.bytheapp.com/privacy</Link>
            </p>

            <p className="privacy-back">
              <Link to="/">← Back to home</Link>
            </p>
          </div>
        </div>
      </main>

      <div id="footer">
        <div className="container text-center">
          <p>
            &copy; 2025 ByTheApp. All rights reserved.{" "}
            <a href="mailto:info@bytheapp.com">info@bytheapp.com</a>
            {" · "}
            <Link to="/privacy">Privacy Policy</Link>
            {" · "}
            <Link to="/terms">Terms</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
